import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { ArrowRight, CircleAlert, Clock, Eye, EyeOff, Info, WifiOff } from "lucide-react"
import * as React from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { isApiError } from "@/api/client"
import { sessionQuery, useLogin } from "@/api/queries/session"
import { BrandMark, BrandWordmark } from "@/components/app/brand"
import { FieldShell } from "@/components/app/fields"
import { Shortcut } from "@/components/app/kbd"
import { LanguageSwitch, ThemeToggle } from "@/components/shell/user-menu"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group"
import { Spinner } from "@/components/ui/spinner"
import { useDocumentTitle } from "@/hooks/use-document-title"
import { useLanguage } from "@/hooks/use-language"
import { isLanguage } from "@/i18n"
import { hasModKey, isTypingTarget } from "@/lib/platform"
import { z } from "@/lib/zod"

interface LoginSearch {
  redirect?: string
  expired?: boolean
}

/** Only allow same-origin relative redirects. */
function safeRedirect(target: string | undefined): string {
  if (!target?.startsWith("/") || target.startsWith("//") || target.startsWith("/login")) return "/"
  return target
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    expired: search.expired === true || search.expired === "true" ? true : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery()).catch(() => null)
    if (session?.authenticated) throw redirect({ href: safeRedirect(search.redirect), replace: true })
  },
  component: LoginPage,
})

const schema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
})
type Values = z.infer<typeof schema>

function LoginPage() {
  const { t } = useTranslation()
  useDocumentTitle(t("login.title"))
  return (
    <div className="bg-background grid min-h-dvh lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <BrandPanel />
      <div className="relative flex min-h-dvh flex-col px-5 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-10">
        <div className="flex items-center justify-between lg:justify-end">
          <BrandWordmark className="text-sm lg:hidden" />
          <div className="flex items-center gap-1">
            <LanguageSwitch />
            <ThemeToggle />
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center py-10">
          <LoginForm />
        </div>
        <p className="text-muted-foreground hidden items-center justify-center gap-2 text-xs sm:flex">
          <Shortcut keys={["/"]} /> {t("login.hintFocus")}
          <span aria-hidden className="mx-1">
            ·
          </span>
          <Shortcut keys={["mod", "enter"]} /> {t("login.hintSubmit")}
        </p>
      </div>
    </div>
  )
}

function LoginForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const login = useLogin()
  const qc = useQueryClient()
  const { setLanguage } = useLanguage()
  const [showPassword, setShowPassword] = React.useState(false)
  const [error, setError] = React.useState<unknown>(null)
  const [cooldown, setCooldown] = React.useState(0)
  const formRef = React.useRef<HTMLFormElement>(null)
  const userRef = React.useRef<HTMLInputElement | null>(null)

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { username: "", password: "" },
  })
  const { errors, isSubmitting } = form.formState

  // rate-limit countdown
  React.useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  // "/" focuses the username, ⌘/Ctrl+Enter submits
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !isTypingTarget(e.target)) {
        e.preventDefault()
        userRef.current?.focus()
      } else if (e.key === "Enter" && hasModKey(e)) {
        e.preventDefault()
        formRef.current?.requestSubmit()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null)
    try {
      await login.mutateAsync(values)
      const session = qc.getQueryData(sessionQuery().queryKey)
      const locale = session?.user?.locale?.slice(0, 2)
      if (locale && isLanguage(locale)) void setLanguage(locale, { persist: false })
      await navigate({ href: safeRedirect(search.redirect), replace: true })
    } catch (e) {
      setError(e)
      if (isApiError(e) && e.isRateLimited) setCooldown(e.retryAfter ?? 60)
      form.setValue("password", "")
      form.setFocus("password")
    }
  })

  const { ref: userFieldRef, ...userField } = form.register("username")
  const busy = isSubmitting || login.isPending

  return (
    <div className="w-full max-w-sm">
      <div className="mb-8 space-y-2">
        <BrandMark className="mb-6 hidden size-10 lg:block" />
        <h1 className="text-2xl font-semibold tracking-tight">{t("login.heading")}</h1>
        <p className="text-muted-foreground text-sm">{t("login.subheading")}</p>
      </div>

      {search.expired && !error && (
        <Alert className="border-info/30 bg-info/5 mb-5">
          <Info className="text-info" />
          <AlertDescription>{t("login.expired")}</AlertDescription>
        </Alert>
      )}
      {error != null && <LoginError error={error} cooldown={cooldown} />}

      <form ref={formRef} onSubmit={onSubmit} noValidate className="grid gap-4" data-testid="login-form">
        <FieldShell id="username" label={t("login.username")} error={errors.username}>
          <Input
            id="username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            placeholder={t("login.usernamePlaceholder")}
            className="h-9"
            aria-invalid={!!errors.username || undefined}
            {...userField}
            ref={(el) => {
              userFieldRef(el)
              userRef.current = el
            }}
          />
        </FieldShell>
        <FieldShell id="password" label={t("login.password")} error={errors.password}>
          <InputGroup className="h-9">
            <InputGroupInput
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••••"
              aria-invalid={!!errors.password || undefined}
              {...form.register("password")}
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
        </FieldShell>
        <Button
          type="submit"
          size="lg"
          className="mt-2 h-9 w-full"
          disabled={busy || cooldown > 0}
          data-testid="login-submit"
        >
          {busy ? <Spinner /> : null}
          {busy
            ? t("login.signingIn")
            : cooldown > 0
              ? t("login.retryIn", { seconds: cooldown })
              : t("login.submit")}
          {!busy && cooldown <= 0 && <ArrowRight className="ml-auto opacity-70" />}
        </Button>
      </form>
      <p className="text-muted-foreground mt-6 text-center text-xs text-pretty">{t("login.help")}</p>
    </div>
  )
}

function LoginError({ error, cooldown }: { error: unknown; cooldown: number }) {
  const { t } = useTranslation()
  let icon = <CircleAlert />
  let title = t("login.errors.failedTitle")
  let description = t("login.errors.failed")
  if (isApiError(error)) {
    if (error.isRateLimited) {
      icon = <Clock />
      title = t("login.errors.rateLimitedTitle")
      description =
        cooldown > 0
          ? t("login.errors.rateLimitedWait", { seconds: cooldown })
          : t("login.errors.rateLimited")
    } else if (error.isNetwork) {
      icon = <WifiOff />
      title = t("login.errors.networkTitle")
      description = t("login.errors.network")
    } else if (error.isUnauthorized) {
      title = t("login.errors.invalidTitle")
      description = t("login.errors.invalid")
    } else if (error.isForbidden) {
      title = t("login.errors.forbiddenTitle")
      description = error.message || t("login.errors.forbidden")
    } else if (error.status >= 500) {
      description = t("errors.server")
    }
  }
  return (
    <Alert
      variant="destructive"
      className="border-destructive/30 bg-destructive/5 mb-5"
      data-testid="login-error"
    >
      {icon}
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
    </Alert>
  )
}

const FLOW = [
  { agent: "planner", key: "login.flow.planner" },
  { agent: "researcher", key: "login.flow.researcher" },
  { agent: "analyst", key: "login.flow.analyst" },
  { agent: "writer", key: "login.flow.writer" },
] as const

/** Agent chips in the palette accents (raspberry, slate, sage, sand), as [tint, text] on the dark panel. */
const AGENT_COLORS: Record<(typeof FLOW)[number]["agent"], readonly [string, string]> = {
  planner: ["rgb(185 31 75 / 0.45)", "#f4b6c6"],
  researcher: ["rgb(74 103 132 / 0.5)", "#c3d4e6"],
  analyst: ["rgb(85 115 95 / 0.5)", "#c4dccb"],
  writer: ["rgb(154 106 79 / 0.5)", "#ebcdb9"],
}

/** Decorative left panel: brand, product promise and an animated multi-agent transcript. */
function BrandPanel() {
  const { t } = useTranslation()
  const [visible, setVisible] = React.useState(0)
  React.useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduce) {
      setVisible(FLOW.length + 1)
      return
    }
    const id = setInterval(() => setVisible((v) => (v > FLOW.length + 3 ? 0 : v + 1)), 1400)
    return () => clearInterval(id)
  }, [])

  return (
    <aside className="relative hidden overflow-hidden bg-[#0d0e10] text-[#f5f5f5] lg:flex lg:flex-col">
      <div
        aria-hidden
        className="absolute inset-0 [background:radial-gradient(60%_50%_at_15%_5%,rgb(185_31_75/0.42),transparent_70%),radial-gradient(55%_45%_at_95%_95%,rgb(74_103_132/0.22),transparent_70%)]"
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.12] [background-image:radial-gradient(rgb(255_255_255/0.5)_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]"
      />
      <div className="relative flex flex-1 flex-col p-10 xl:p-14">
        <div className="flex items-center gap-2.5 text-sm font-semibold tracking-tight">
          <BrandMark fixed className="size-8" />
          <span>
            dotlogic <span className="font-medium text-[#9a9da3]">agents</span>
          </span>
        </div>

        <div className="flex flex-1 flex-col justify-center gap-10 py-12">
          <div className="max-w-md space-y-3">
            <h2 className="text-3xl font-semibold tracking-tight text-balance xl:text-4xl">
              {t("login.brand.headline")}
            </h2>
            <p className="text-sm text-pretty text-[#9a9da3] xl:text-base">{t("login.brand.subline")}</p>
          </div>

          <div className="max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-4 shadow-2xl backdrop-blur-sm">
            <div className="mb-3 flex items-center justify-between text-xs text-[#9a9da3]">
              <span className="font-mono">{t("login.flow.title")}</span>
              <span className="flex items-center gap-1.5">
                <span className="relative flex size-1.5">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-[#6ebe96] opacity-60" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-[#6ebe96]" />
                </span>
                {t("login.flow.live")}
              </span>
            </div>
            <ol className="space-y-2.5" aria-hidden>
              {FLOW.map((line, i) => (
                <li
                  key={line.agent}
                  className="flex items-start gap-2.5 text-[0.8125rem] transition-all duration-500"
                  style={{
                    opacity: visible > i ? 1 : 0,
                    transform: visible > i ? "none" : "translateY(4px)",
                  }}
                >
                  <span
                    className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md text-[0.625rem] font-semibold uppercase"
                    style={{
                      backgroundColor: AGENT_COLORS[line.agent][0],
                      color: AGENT_COLORS[line.agent][1],
                    }}
                  >
                    {t(`login.flow.agents.${line.agent}`).slice(0, 1)}
                  </span>
                  <span className="text-[#d4d6da]">
                    <span className="font-medium text-[#f5f5f5]">{t(`login.flow.agents.${line.agent}`)}</span>{" "}
                    {t(line.key)}
                  </span>
                </li>
              ))}
              <li
                className="flex items-center gap-2 pt-1 text-xs text-[#6ebe96] transition-opacity duration-500"
                style={{ opacity: visible > FLOW.length ? 1 : 0 }}
              >
                <span aria-hidden>✓</span> {t("login.flow.done")}
              </li>
            </ol>
          </div>
        </div>

        <div className="flex items-center gap-6 text-xs text-[#75787f]">
          <span>{t("login.brand.pillars.secure")}</span>
          <span>{t("login.brand.pillars.private")}</span>
          <span>{t("login.brand.pillars.open")}</span>
        </div>
      </div>
    </aside>
  )
}
